import React from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import pluralize from 'pluralize';
import useViewportSizes from 'use-viewport-sizes';

import Link from '@components/shared/Link';
import Rating from '@components/shared/Rating';
import ProductThumbnail from '@components/shared/ProductThumbnail';
import ActionIcon from '@components/shared/ActionIcon';
import HandpickedCure from '@components/Cure/HandpickedCure';
import { useCureFavorites } from '@hooks/useCureFavorites';
import { cureCardsTypes, tooltips, lgStartBreakpoint } from '@constants/common';
import routes from '@constants/routes';
import { curePropTypes } from '@components/Cure/types';

const Cure = ({
  cure,
  type = 'vertical',
  className,
  needUnlikeConfirm,
  onUnlikeSuccess
}) => {
  const [viewportWidth] = useViewportSizes(500);
  const creator = get(cure, 'creator');
  const condition = get(cure, 'disease_condition.name');

  const { isFavorited, handleToggleFavorite } = useCureFavorites(
    cure,
    needUnlikeConfirm,
    onUnlikeSuccess
  );

  const isHandpicked = get(cure, 'is_curerate_choice');
  const Wrapper = isHandpicked ? HandpickedCure : React.Fragment;

  return (
    <Wrapper>
      <div
        className={classNames('cure', { [`cure--${type}`]: type }, className)}
      >
        <ActionIcon
          type="favorite"
          isActive={isFavorited}
          className="cure__favorite"
          onClick={handleToggleFavorite}
          tooltip={
            isFavorited ? tooltips.removeFromFavorites : tooltips.addToFavorites
          }
        />

        <Link
          href={routes.cures.details.pattern}
          as={routes.cures.details.path(cure.slug)}
          className="cure__media"
        >
          <ProductThumbnail src={cure.thumbnail} className="cure__img" />
        </Link>

        <div className="cure__content">
          <div className="cure__body">
            <div className="cure__header">
              <Link
                href={routes.cures.details.pattern}
                as={routes.cures.details.path(cure.slug)}
                className="cure__title"
              >
                {cure.name}
              </Link>
            </div>
            {type === cureCardsTypes.horizontal && creator && (
              <p className="cure__author">
                by{' '}
                {(creator.is_admin && <span>CureRate</span>) || (
                  <Link
                    href={routes.profile.public.pattern}
                    as={routes.profile.public.path(creator.id)}
                  >
                    {creator.username}
                  </Link>
                )}
              </p>
            )}
          </div>
          {type === cureCardsTypes.vertical && (
            <hr className="cure__header-hr" />
          )}
          {type === cureCardsTypes.vertical && (
            <Link
              href={routes.cures.details.pattern}
              as={routes.cures.details.path(cure.slug)}
              className="cure__rating-link"
            >
              <div className="cure__rating">
                <div className="cure__rating--score">
                  <Rating
                    iconSize={viewportWidth < lgStartBreakpoint ? 13 : 19}
                    value={cure.avg_score}
                    name={`cure-${cure.slug}`}
                  />
                  <div className="cure__count">
                    <span>{cure.avg_score}</span> / 5.0
                  </div>
                </div>
                <div className="cure__reviews-count">
                  <span className="large">{cure.reviews_number}</span>{' '}
                  {pluralize('review', cure.reviews_number)}
                </div>
                <div className="cure__reviews-condition">
                  from people with {condition}
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

Cure.propTypes = curePropTypes;

export default Cure;
